import {aiPromptBoxInitialState} from '@Components/ai-prompt-box/ai-prompt-box-library';
import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {
  AiPromptBoxAddAttachmentsAction,
  AiPromptBoxAddLoadingAttachmentsAction,
  AiPromptBoxAddPendingImageAttachmentsAction,
  AiPromptBoxInitializationAction,
  AiPromptBoxMarkPendingAttachmentFailedAction,
  AiPromptBoxMarkPendingAttachmentUploadingAction,
  AiPromptBoxReduxState,
  AiPromptBoxRemoveImageAttachmentsAction,
  AiPromptBoxRemoveLoadingAttachmentsAction,
  AiPromptBoxRemovePendingAttachmentAction,
  AiPromptBoxResolvePendingAttachmentAction,
  AiPromptBoxUpdateAction,
} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {getAttachmentUniqueKey, MAX_ATTACHMENTS} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import type {PromptAttachment, UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PendingUploadStatus, PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {isPendingImageAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {deepAssign} from '@Utils/misc.util';

const deduplicateAttachments = (attachments: PromptAttachment[]): PromptAttachment[] => {
  const seenKeys = new Set<string>();

  return attachments.filter((attachment) => {
    const key = getAttachmentUniqueKey(attachment);

    if (seenKeys.has(key)) {
      return false;
    }

    seenKeys.add(key);
    return true;
  });
};

const aiPromptBox = createSlice({
  name: 'aiPromptBox',
  initialState: aiPromptBoxInitialState(),
  reducers: {
    initializeAiPromptBox: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxInitializationAction>): void => {
      const {id} = action.payload;
      state[id] = action.payload.value;
    },
    updateAiPromptBox: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxUpdateAction>): void => {
      const {id, value} = action.payload;
      const entity = state[id];

      if (!entity) {
        return;
      }

      deepAssign(entity, value);
    },
    addAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxAddAttachmentsAction>): void => {
      const {id, attachments, loadingAttachments, prefillPromptIfEmpty} = action.payload;
      const entity = state[id];

      if (!entity) {
        return;
      }

      const currentItems = entity.attachments?.items ?? [];
      const uniqueItems = deduplicateAttachments([...currentItems, ...attachments]).slice(0, entity.attachments?.maxAttachments ?? MAX_ATTACHMENTS);

      entity.attachments = {
        items: uniqueItems,
        dropdown: entity.attachments?.dropdown ?? Object.values(PromptAttachmentType),
        maxAttachments: entity.attachments?.maxAttachments,
      };

      if (loadingAttachments !== undefined) {
        entity.attachmentLoaders = loadingAttachments;
      }

      if (prefillPromptIfEmpty !== undefined && entity.prompt.trim() === '') {
        entity.prompt = prefillPromptIfEmpty;
      }
    },
    addLoadingAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxAddLoadingAttachmentsAction>): void => {
      const {id, loadingAttachments} = action.payload;
      const entity = state[id];

      if (!entity) {
        return;
      }

      entity.attachmentLoaders = [...(entity.attachmentLoaders ?? []), ...loadingAttachments];
    },
    removeLoadingAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxRemoveLoadingAttachmentsAction>): void => {
      const {id, loadingIds} = action.payload;
      const entity = state[id];

      if (!entity || loadingIds.length === 0) {
        return;
      }

      entity.attachmentLoaders = (entity.attachmentLoaders ?? []).filter((loader) => !loadingIds.includes(loader.id));
    },
    removeImageAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxRemoveImageAttachmentsAction>): void => {
      const {id, hashedFilenames} = action.payload;
      const entity = state[id];

      if (!entity || hashedFilenames.length === 0 || !entity.attachments) {
        return;
      }

      const hashedSet = new Set(hashedFilenames);
      entity.attachments.items = entity.attachments.items.filter((item) => {
        if (item.type !== PromptAttachmentType.IMAGE && item.type !== PromptAttachmentType.CAPTURE_PHOTO) {
          return true;
        }
        return !hashedSet.has(item.hashedFilename);
      });
    },
    addPendingImageAttachments: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxAddPendingImageAttachmentsAction>): void => {
      const {id, attachments} = action.payload;
      const entity = state[id];

      if (!entity || attachments.length === 0) {
        return;
      }

      const currentItems = entity.attachments?.items ?? [];
      const uniqueItems = deduplicateAttachments([...currentItems, ...attachments]).slice(0, entity.attachments?.maxAttachments ?? MAX_ATTACHMENTS);

      entity.attachments = {
        items: uniqueItems,
        dropdown: entity.attachments?.dropdown ?? Object.values(PromptAttachmentType),
        maxAttachments: entity.attachments?.maxAttachments,
      };
    },
    resolvePendingAttachment: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxResolvePendingAttachmentAction>): void => {
      const {id, localId, resolved} = action.payload;
      const entity = state[id];

      if (!entity?.attachments) {
        return;
      }

      const items = entity.attachments.items;
      const index = findPendingAttachmentIndex(items, localId);
      if (index === -1) {
        return;
      }

      items[index] = resolved;
    },
    markPendingAttachmentFailed: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxMarkPendingAttachmentFailedAction>): void => {
      const {id, localId} = action.payload;
      const entity = state[id];

      if (!entity?.attachments) {
        return;
      }

      const target = findPendingImageAttachment(entity.attachments.items, localId);
      if (!target?.pendingUpload) {
        return;
      }

      target.pendingUpload.status = PendingUploadStatus.FAILED;
    },
    markPendingAttachmentUploading: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxMarkPendingAttachmentUploadingAction>): void => {
      const {id, localId} = action.payload;
      const entity = state[id];

      if (!entity?.attachments) {
        return;
      }

      const target = findPendingImageAttachment(entity.attachments.items, localId);
      if (!target?.pendingUpload) {
        return;
      }

      target.pendingUpload.status = PendingUploadStatus.UPLOADING;
    },
    removePendingAttachment: (state: AiPromptBoxReduxState, action: PayloadAction<AiPromptBoxRemovePendingAttachmentAction>): void => {
      const {id, localId} = action.payload;
      const entity = state[id];

      if (!entity?.attachments) {
        return;
      }

      entity.attachments.items = entity.attachments.items.filter((item) => {
        return !isPendingImageAttachment(item) || item.localId !== localId;
      });
    },
  },
});

const findPendingAttachmentIndex = (items: PromptAttachment[], localId: string): number => {
  return items.findIndex((item) => isPendingImageAttachment(item) && item.localId === localId);
};

const findPendingImageAttachment = (items: PromptAttachment[], localId: string): UserImageAttachment | undefined => {
  const index = findPendingAttachmentIndex(items, localId);
  if (index === -1) {
    return undefined;
  }
  const found = items[index];
  return isPendingImageAttachment(found) ? found : undefined;
};

export const aiPromptBoxReducer = aiPromptBox.reducer;
export const aiPromptBoxActions = aiPromptBox.actions;
